import { LitElement, html, css, PropertyValues, TemplateResult } from 'lit';
import {property, customElement} from 'lit/decorators.js';
import {repeat} from 'lit/directives/repeat.js';
import { voyageCursorStyles } from '../../voyage-style/src/voyage-style';
import { SvgIcons } from '../../voyage-svg-icons/src/voyage-svg-icons';
import '../../voyage-popup/src/voyage-popup';
import { OffTrackAlarmReasons, SpeedAlarmReasons, StoppageAlarmReasons } from '../../voy-handle-alarm-popup/src/types';

export enum VoyDropDownEvents {
    ItemSelected = "item-selected"
}

@customElement('voy-dropdown')
export class VoyDropdown extends LitElement {
    @property({type: Array, attribute: "dropdown-data"}) dropdownData: string[] = [];
    @property({type: String, attribute: "placeholder"}) placeholder: string = "Select an option";
    @property({type: String}) customDisplayText: string | null = null;
    @property({type: Boolean}) enableSearch: boolean = false;
    @property({type: Boolean}) enableSorting: boolean = false;
    @property({type: String}) initialSelectedItem: string|null = null;
    
    @property({attribute: false}) private _showDropdown: boolean = false;
    @property({attribute: false}) private _shownList: string[] = [];
    @property({attribute: false}) private _selectedItem: string | null = null;

    constructor() {
        super();
    }

    static get styles()
    {
        return [
            voyageCursorStyles,
            css`
            :host {
                box-sizing: border-box;
                position: relative;
                width: 100%;
                border: 1px solid var(--neutrals-grey-07);
                border-radius: 4px;
                height: 40px;
            }

            .container {
                box-sizing: border-box;
                font-family: MaerskText;
                background: #FFFFFF;
                padding: 3px;
                display: flex;
                align-items: center;
                justify-content: space-between;
                cursor: pointer;
                width: 100%;
                height: 100%;
                padding: 5px 10px 5px 10px;
                background: none;
            }

            .container button {
                color: var(--neutrals-grey-02);
                font-size: 14px;
                padding: 0;
            }

            .dropdown-button {
                text-align: left;
                user-select: none;
                width: 100%;
                font-family: MaerskText;
                background: #FFFFFF;
                border: none;
                outline: none;
            }

            .dropdown-svg {
                justify-self: end;
                cursor: pointer;
            }

            .dropdown-svg > svg{
                color: var(--neutrals-grey-04, black);
                width: 9px;
                height: 14px;
                rotate: 90;
            }

            .dropdown-content {
                box-sizing: border-box;
                background: #FFFFFF;
                border: 1px solid var(--neutrals-grey-07);
                border-radius: 2.5px;
                box-shadow: 0px 4px 12px rgba(20, 20, 20, 0.06), 0px 2px 4px rgba(20, 20, 20, 0.1);
                width: 100%;
                max-height: 145px;
                display: block;
                background: #FFFFFF;
                overflow: auto;
                z-index: 4;
                position: absolute;
            }

            .dropdown-content[hidden] {
                display: none;
            }

            .search-result {
                box-sizing: border-box;
                width: 100%;
                cursor: pointer;
                padding: 5px;
                text-align: left;
                user-select: none;
                background: #FFFFFF;
                border: none;
                outline: none;
                font-family: MaerskText;
            }

            .search-result:hover {
                background: rgba(0, 84, 135, 0.1);
            }

            .search-input {
                background: #FFFFFF;
                font-family: MaerskText;
                border: none;
                outline: none;
                width: 100%;
                height: 100%;
                color: var(--neutrals-grey-02);
            }

            .search-box-container {
                display: grid;
                place-items: center;
            }

            .placeholder {
                color: var(--neutrals-grey-04, grey);
            }
        `
        ];
    }

    private _isItemDisabled(item: string): boolean {
        return (
            item === StoppageAlarmReasons.SelectReasonForStoppage ||
            item === OffTrackAlarmReasons.SelectReasonForOfftrack ||
            item === SpeedAlarmReasons.SelectReasonForSpeed
        );
    }

    render() {
        return html`
            <div class="container" tabindex="-1" @click=${this._toggleDropdown}>
                ${this.enableSearch ? this._displaySearchInput() : this._displayDefault()}
                <div class="dropdown-svg">${this._showDropdown ? html`${SvgIcons.lightChevronUp}` : html`${SvgIcons.lightChevronDown}`}</div>
            </div>
            <div id="dropdown" class="dropdown-content" ?hidden=${!this._showDropdown}>
                ${repeat(this._shownList, (item, index) => {
                    return html`
                      <button
                        ?disabled=${this._isItemDisabled(item)}
                        class="search-result"
                      >
                        ${item}
                      </button>
                    `;})}
            </div>
        `;
    }

    firstUpdated() {
        this._applyFilter("");
    }

    updated(changedProperties: PropertyValues) {
        if (changedProperties.has("dropdownData") && this.dropdownData !== null && this.dropdownData.length > 0) {
            this._applyFilter("");
        }
        if (changedProperties.has("initialSelectedItem") && this.initialSelectedItem) {
            this._selectedItem = this.initialSelectedItem;
        }
        super.updated(changedProperties);
    }

    resetSelected() {
        this._selectedItem = null;
    }

    private _displaySearchInput() {
        return (
            html`<input type="text" 
                        class="search-input display"
                        .value=${this._selectedItem ? this._selectedItem : ""}
                        placeholder="Search.."
                        @input=${this._onSearchInput}
                        @blur=${(e: FocusEvent)=>this._onSearchBlur(e)}
                >`
        )
    }

    private _displayDefault() {
        return (
            html`<button class="dropdown-button display"
                        @blur=${this._onSearchBlur}>${this._displayText()}
                </button>`
        )
    }

    private _displayText():TemplateResult {
        if (this.customDisplayText) return html`${this.customDisplayText}`
        if (this._selectedItem) return html`${this._selectedItem}`;
        return html`<div class="placeholder">${this.placeholder}</div>`;
    }

    private _onSearchBlur(e: FocusEvent) {
        // handle item clicked
        const dropdownItems = this.shadowRoot!.querySelector(".dropdown-content");
        const itemClicked = dropdownItems!.contains(<HTMLElement>e.relatedTarget); // relatedTarget is the element to which the current element loses focus
        if (itemClicked) {
            this._selectedItem = (<HTMLElement>e.relatedTarget).textContent;
            this.dispatchEvent(new CustomEvent(VoyDropDownEvents.ItemSelected, { detail: { selectedItem: this._selectedItem } }));
            this._showDropdown = false;
            return;
        }

        // click on dropdown, let toggleDropdown() handle the collapsing
        const container = this.shadowRoot!.querySelector(".container");
        const containerClicked = container!.contains(<HTMLElement>e.relatedTarget); // relatedTarget is the element to which the current element loses focus
        if (containerClicked) {
            return
        }
        // clicked outside dropdown, so collapse it
        this._showDropdown = false;
    }

    private _toggleDropdown() {
        this._showDropdown = !this._showDropdown;
        if (this._showDropdown) {
            const display = <HTMLInputElement>this.shadowRoot!.querySelector(".display");
            display!.focus();
        }
    }

    private _onSearchInput(evt: { target: { value: string } }) {
        this._applyFilter(evt.target.value);
    }

    private _applyFilter(filterText: string) {
        const filteredList = this._shownList = this.dropdownData
        .filter(item => item.toLocaleLowerCase().includes(filterText.toLocaleLowerCase()))

        if (this.enableSorting) {
            filteredList.sort((a, b) => {
                return a.localeCompare(b);
            });
        }
        
        return filteredList
    }
};
